import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import ErrorUtils from 'src/utils/ErrorUtils';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { ACTIONS } from 'src/services/segment-cdt/SegmentTablesReducer';
import { SegmentPeriodRequest } from 'src/services/segment-cdt/SegmentDataTablesService';
import { ColumnPullStatus, PullStatusResponse } from 'src/models/segment-cdt/SegmentCdtTypes';
import { PullInProgressError } from 'src/models/segment-cdt/PullInProgressError';
import SegmentValidationModal from 'src/components/segment-cdt/detail/SegmentValidationModal';
import SegmentUploadModal from 'src/components/segment-cdt/detail/SegmentUploadModal';
import SegmentAddColumnModal from 'src/components/segment-cdt/detail/SegmentAddColumnModal';

const PULL_POLL_MS = 3000;
// Cap the poll so a status that never reaches a terminal state (or a backend that keeps omitting
// pullStatus for an unknown/expired id) can't spin the button forever. 3s * 200 ≈ 10 minutes, well past
// a normal pull. Mirrors the shared promotion poller's MAX_PROMOTION_POLL_ATTEMPTS safeguard.
const MAX_PULL_POLL_ATTEMPTS = 200;
const isTerminal = (s?: PullStatusResponse) =>
    s?.overallStatus === ColumnPullStatus.COMPLETED || s?.overallStatus === ColumnPullStatus.FAILED;

/**
 * The detail viewer's action toolbar: Pull values (trigger + poll GL pull), Upload (file modal),
 * Download (presigned URL → open), Validate (on-demand report modal). "Add columns" stays a stub for a
 * later CR. Reads `{ state, dispatch, services }` from context; period comes from the detail slice.
 */
export default function SegmentDetailToolbar() {
    const { state, dispatch, services } = useContext(SegmentTablesContext);
    const svc = services.segmentDataTablesService;
    // tableId is the decoded route param, published to the detail slice by SegmentTableDetailContent —
    // read from context (reliable + immediate) rather than threaded through props.
    const tableId = state.detail.tableId ?? '';
    const period = state.detail.selectedPeriod;
    // Latest selected period, readable from inside an async handler (whose closure otherwise captures the
    // stale click-time period) so a pull triggered for one period can be dropped if the user switches away.
    // Written during render (not in an effect) so it can't lag a period-switch commit — the poll's
    // period-match guard reads it and a deferred effect would leave a sub-frame window of misattribution.
    const periodRef = useRef(period);
    periodRef.current = period;

    const [showValidate, setShowValidate] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [showAddColumns, setShowAddColumns] = useState(false);
    // Setting these (to a fresh object) fires the corresponding render-hook; identity change = re-run.
    const [validateReq, setValidateReq] = useState<SegmentPeriodRequest | undefined>(undefined);
    const [downloadReq, setDownloadReq] = useState<SegmentPeriodRequest | undefined>(undefined);
    // The id of the GL pull currently being polled; null = idle. Mirrors usePromotion's
    // inProgressPromotionId: set from the mount/period lookup or a fresh trigger, cleared on a terminal
    // status. `triggering` covers the brief trigger call before an id exists.
    const [pullId, setPullId] = useState<string | null>(null);
    const [triggering, setTriggering] = useState(false);
    // True until the mount/period-change lookup resolves. Like usePromotion's `initializing`, it keeps
    // Pull disabled during the initial lookup so a click can't race a late "is a pull already running?"
    // answer.
    const [initializing, setInitializing] = useState(false);
    // True from the Validate click until the fresh report for the requested period arrives, so a
    // re-open never flashes the previous period's report before the spinner.
    const [awaitingValidate, setAwaitingValidate] = useState(false);
    // Set synchronously on the Download click so the button disables in the same render, before the
    // fetch hook's post-render effect flips `downloading`. Without it a fast double-click fires two
    // fetches and opens two tabs. Mirrors awaitingValidate.
    const [awaitingDownload, setAwaitingDownload] = useState(false);

    const [validation, validating, validateError] = svc.getSegmentValidations(validateReq);
    const [download, downloading, downloadError] = svc.downloadSegmentDataTable(downloadReq);

    // Set once the component unmounts so an awaited handler (onPull) doesn't setState on a dead component.
    const unmounted = useRef(false);
    useEffect(() => () => { unmounted.current = true; }, []);

    // Drop the awaiting-flash gate once the validate fetch settles: when a report/error arrives, OR when a
    // fetch we saw go in-flight has finished — the latter clears the spinner even for a resolved-but-empty
    // report (which is otherwise falsy with no error), instead of hanging on it.
    const validateInFlight = useRef(false);
    useEffect(() => {
        if (validating) {
            validateInFlight.current = true;
            return;
        }
        if (validation || validateError || validateInFlight.current) {
            validateInFlight.current = false;
            setAwaitingValidate(false);
        }
    }, [validation, validateError, validating]);

    // Download: when a presigned URL arrives, trigger the download and clear the request so the same
    // period can be downloaded again later. Use a temporary anchor rather than window.open — the URL
    // resolves after an async fetch, outside the click's user-activation window, so window.open would be
    // popup-blocked; an <a> click is not.
    // Drop the download gate once the fetch settles: when a URL/error arrives, OR when a fetch we saw go
    // in-flight has finished (clears the gate even for a resolved-but-empty response instead of hanging).
    // Mirrors the validate in-flight effect.
    const downloadInFlight = useRef(false);
    useEffect(() => {
        if (downloading) {
            downloadInFlight.current = true;
            return;
        }
        if (download?.downloadUrl || downloadError || downloadInFlight.current) {
            downloadInFlight.current = false;
            setAwaitingDownload(false);
        }
    }, [download, downloadError, downloading]);

    useEffect(() => {
        if (download?.downloadUrl) {
            const a = document.createElement('a');
            a.href = download.downloadUrl;
            // Open in a new tab: a presigned S3 URL is cross-origin (so the `download` attribute is ignored)
            // and may lack Content-Disposition, in which case a same-tab navigation would unload the app —
            // target="_blank" keeps the detail view intact whether the URL downloads or renders.
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setDownloadReq(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [download]);
    useEffect(() => {
        if (downloadError) {
            services.messageService.showErrorBanner(downloadError);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downloadError]);

    const needPeriod = (): boolean => {
        if (!period) {
            services.messageService.showErrorBanner('Select a period first.');
            return false;
        }
        return true;
    };

    const onValidate = () => {
        if (!needPeriod()) return;
        setAwaitingValidate(true); // spinner until this period's fresh report arrives
        setValidateReq({ tableId, period: period as string }); // fresh object → re-fetch
        setShowValidate(true);
    };

    const onDownload = () => {
        if (!needPeriod()) return;
        setAwaitingDownload(true); // disable synchronously so a fast double-click can't open two tabs
        setDownloadReq({ tableId, period: period as string });
    };

    // ── Mount / period-change lookup ──────────────────────────────────────────────────────────────
    // Discover a pull already in flight for this table+period (the backend reports it by tableId+period
    // for a caller — like a reload — that no longer holds the id). Like PromoteButton's initial
    // GetPromotionStatus: silent, no banner, it only decides whether the button already shows "Pulling…".
    // Resetting pullId to null up front stops a prior period's poll on a period switch; the lookup then
    // ONLY sets an id when a pull is genuinely in flight, so a late "idle" answer can't clobber a pull the
    // user just triggered (the button is disabled while `initializing`, so no click races the lookup).
    useEffect(() => {
        setPullId(null);
        if (!period || !tableId) {
            setInitializing(false);
            return;
        }
        let cancelled = false;
        setInitializing(true);
        svc.getCDTGLPullStatusByTable(tableId, period as string)
            .then((status) => {
                if (cancelled) return;
                if (status && !isTerminal(status) && status.pullRequestId) {
                    setPullId(status.pullRequestId);
                }
                setInitializing(false);
            })
            .catch(() => { if (!cancelled) setInitializing(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableId, period]);

    // ── Poll while a pull is in flight (pullId set) ───────────────────────────────────────────────
    // Capped so a stuck/unknown pull can't spin the button forever (mirrors MAX_PROMOTION_POLL_ATTEMPTS).
    // Cancels on unmount and whenever pullId changes (e.g. a period switch), so a completed pull for one
    // period can't refresh another.
    useEffect(() => {
        if (!pullId) return;
        // The period this pull belongs to. The [pullId] effect's cleanup lags one commit on a period
        // switch, so guard the terminal branch: if the user has moved to another period by the time the
        // pull finishes, stop tracking it but don't banner/refresh the period they're now viewing.
        const pollPeriod = periodRef.current;
        let cancelled = false;
        let attempts = 0;
        let timer: ReturnType<typeof setTimeout> | null = null;
        const poll = async () => {
            attempts += 1;
            try {
                const status = await svc.getCDTGLPullStatus(pullId);
                if (cancelled) return;
                if (isTerminal(status)) {
                    setPullId(null);
                    if (periodRef.current !== pollPeriod) return; // user switched periods — not their pull to announce
                    const upserted = status!.rowsUpserted ?? 0;
                    if (status!.overallStatus === ColumnPullStatus.COMPLETED) {
                        services.messageService.showSuccessAutoDismissBanner(`GL pull complete. ${upserted} row(s) updated.`);
                        dispatch(ACTIONS.REFRESH_DETAIL_ROWS);
                    } else {
                        // FAILED can be partial (some columns COMPLETED, rows upserted) — surface the error,
                        // but still refresh if any rows were written so those values aren't left hidden.
                        services.messageService.showErrorBanner('GL pull failed. Check the column pull statuses.');
                        if (upserted > 0) dispatch(ACTIONS.REFRESH_DETAIL_ROWS);
                    }
                    return;
                }
            } catch (ex) {
                if (cancelled) return;
                const status = (ex as { status?: number } | undefined)?.status;
                // A definitive client error — an unknown/expired pullRequestId (417) or a 403 — never
                // resolves, so stop polling and surface the server's message rather than spinning to the
                // ~10-min cap and then mis-describing it as "taking longer than expected". Throttling and
                // timeout (429/408) are transient and must keep retrying.
                const isDefinitive = typeof status === 'number'
                    && status >= 400 && status < 500 && status !== 408 && status !== 429;
                if (isDefinitive) {
                    setPullId(null);
                    // Don't announce for a period the user has already left (the [pullId] cleanup lags one
                    // commit on a period switch) — mirror the terminal-success branch's guard.
                    if (periodRef.current !== pollPeriod) return;
                    services.messageService.showErrorBanner(
                        ErrorUtils.getMessage(ex) || 'GL pull status is unavailable.');
                    return;
                }
                // Transient (5xx / network / throttle): keep polling, bounded by the attempt cap. WARN so an
                // erroring status endpoint is visible to oncall rather than silently absorbed.
                // eslint-disable-next-line no-console
                console.warn('GL pull status poll failed; will retry', ex);
            }
            if (cancelled) return;
            if (attempts >= MAX_PULL_POLL_ATTEMPTS) {
                setPullId(null);
                services.messageService.showErrorBanner('GL pull is taking longer than expected. Check the column pull statuses and try again.');
                return;
            }
            timer = setTimeout(poll, PULL_POLL_MS);
        };
        poll();
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pullId]);

    const onPull = async () => {
        if (!needPeriod()) return;
        const requestedPeriod = period;
        setTriggering(true);
        try {
            const id = await svc.triggerCDTGLPull(tableId, requestedPeriod as string);
            // Drop the result if the toolbar unmounted or the user switched periods mid-trigger — otherwise
            // we'd poll the old period's pull and refresh the now-current, different period on completion.
            if (unmounted.current || periodRef.current !== requestedPeriod) return;
            setPullId(id || null); // hands off to the polling effect; `|| null` so an empty id can't stick the button
        } catch (ex) {
            // Drop the result if the toolbar unmounted or the user switched periods mid-trigger — otherwise a
            // 409-resume or an error would be attributed to (and poll) the period they are now viewing.
            if (unmounted.current || periodRef.current !== requestedPeriod) return;
            if (ex instanceof PullInProgressError) {
                // A pull is already running (started in another tab, or missed by the mount lookup on a
                // timing edge). Attach to it and resume polling rather than showing a generic failure.
                setPullId(ex.pullRequestId);
            } else {
                services.messageService.showErrorBanner(ErrorUtils.getMessage(ex) || 'Could not start the GL pull.');
            }
        } finally {
            if (!unmounted.current) setTriggering(false);
        }
    };

    return (
        <>
            <SpaceBetween direction="horizontal" size="xs">
                <Button loading={triggering || !!pullId} disabled={initializing || triggering || !!pullId} onClick={onPull}>Pull values</Button>
                <Button iconName="upload" onClick={() => setShowUpload(true)}>Upload</Button>
                <Button iconName="download" loading={downloading || awaitingDownload} disabled={downloading || awaitingDownload} onClick={onDownload}>Download</Button>
                <Button loading={validating || awaitingValidate} disabled={validating || awaitingValidate} onClick={onValidate}>Validate</Button>
                {/* Disabled until the definition GET resolves: the add session seeds the segment (for the
                    CoA lookups) from the definition, so opening before it loads would leave the segment unset. */}
                <Button iconName="add-plus" disabled={!state.detail.definition} onClick={() => setShowAddColumns(true)}>Add columns</Button>
            </SpaceBetween>
            <SegmentValidationModal
                visible={showValidate}
                report={awaitingValidate ? undefined : validation?.report}
                loading={validating || awaitingValidate}
                error={awaitingValidate ? undefined : validateError}
                onDismiss={() => setShowValidate(false)}
            />
            {/* Reads its own state/dispatch from context and handles the post-upload refresh/period-switch
                itself — no prop chain. */}
            <SegmentUploadModal visible={showUpload} onDismiss={() => setShowUpload(false)} />
            {/* Reuses the create-wizard column editor (chooser + GL/Manual/Derived forms + queued-columns
                slice) and commits via AddSegmentColumns, then refreshes the grid from the response. */}
            <SegmentAddColumnModal visible={showAddColumns} onDismiss={() => setShowAddColumns(false)} />
        </>
    );
}
